import {
  Component,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  OnInit, OnDestroy
} from '@angular/core';
import {MatCheckboxChange} from "@angular/material/checkbox";
import {
  DataTable,
  DataTableClass,
  DataTableColumn,
  DataTableRowItemButton,
  DataTableRowItemType
} from './data-table.interfaces';
import {cloneDeep} from 'lodash-es';
import {UtilService} from '../../services/util/util.service';
import {ChildSelectionState} from '../../services/cart/cart-base.service';
import {CartInterface} from '../../services/cart/cart.interface';
import {Cart} from '../../generated/sales-terminal-client';
import {Subscription} from "rxjs";
import {AuthService} from '../../services/auth/auth.service';

@Component({
  selector: 'app-data-table',
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DataTableComponent implements OnInit, OnDestroy {
  public DataTableRowItemType = DataTableRowItemType;

  private _data: any[];
  @Input()
  set data(value: any[]) {
    this._data = value;
    this.renderUI();
    this.dataChange.emit(this._data);
  };
  get data(): any[] {
    return this._data;
  }
  @Output() dataChange: EventEmitter<any[]> = new EventEmitter<any[]>();

  private _config: DataTable;
  @Input()
  set config(value: DataTable) {
    this._config = value;
    this.renderUI();
    this.configChange.emit(this._config);
  };
  get config(): DataTable {
    return this._config;
  }
  @Output() configChange: EventEmitter<DataTable> = new EventEmitter<DataTable>();

  private _selected: any[] = [];
  @Input()
  set selected(value: any[]) {
    this._selected = value;
    this.renderUI();
    this.selectedChange.emit(this._selected);
  }
  get selected(): any[] {
    return this._selected;
  }
  @Output() selectedChange: EventEmitter<any[]> = new EventEmitter<any[]>();

  @Input() public useCartSelectionMap: boolean;
  @Input() public cartService: CartInterface<Cart>;

  private _clickable: any[] = [];
  @Input()
  set clickable(value: any[]) {
    this._clickable = value;
    this.renderUI();
    this.clickableChange.emit(this._clickable);
  }
  get clickable(): any[] {
    return this._clickable;
  }
  @Output() clickableChange: EventEmitter<any[]> = new EventEmitter<any[]>();

  @Input() public useResponsive: boolean;

  private _footerData: any;
  @Input()
  set footerData(value: any) {
    this._footerData = value;
    this.renderUI();
    this.footerDataChange.emit(this._footerData);
  };
  get footerData(): any[] {
    return this._footerData;
  }
  @Output() footerDataChange: EventEmitter<any> = new EventEmitter<any>();

  private _subs: Subscription[] = [];

  constructor(private changeRef: ChangeDetectorRef,
              public utilService: UtilService,
              public authService: AuthService) {}

  public ngOnInit(): void {
    this._subs.push(this.utilService.windowResizeHelper.subscribe(() => {
      this.renderUI();
    }));
  }

  public ngOnDestroy(): void {
    this._subs.forEach(sub => sub.unsubscribe());
    this._subs = [];
  }

  public get areAllSelected(): boolean {
    if (this.useCartSelectionMap) {
      return this.cartService.areAllTicketsSelected;
    }
    return this.selected.length === this.clickable.length;
  }

  public get areSomeSelected(): boolean {
    if (this.useCartSelectionMap) {
      return !this.cartService.areAllTicketsSelected && this.cartService.hasSelectedTickets;
    }
    return !!(this.selected.length && !this.areAllSelected);
  }

  public isClickable(id: number): boolean {
    return this.useCartSelectionMap || this.clickable.some(item => item === id);
  }

  public isSelected(id: number, extraId?: number): boolean {
    if (this.useCartSelectionMap) {
      return this.cartService.selectionMap.events[extraId].tickets[id] === ChildSelectionState.ALL;
    }
    return this.selected.some(item => item === id);
  }

  public toggleAll(checkboxChange: MatCheckboxChange): void {
    if (this.useCartSelectionMap) {
      if (checkboxChange.checked) {
        this.cartService.selectAllTickets();
      } else {
        this.cartService.populateSelectionMap(ChildSelectionState.NONE);
      }
      this.cartService.updateTotalScrapSum();
      this.renderUI();
    } else {
      if (checkboxChange.checked) {
        this.selected = cloneDeep(this.clickable);
      } else {
        this.selected = [];
      }
    }
  }

  public toggleItem(checkboxChange: MatCheckboxChange, value: any, extraValue?: any): void {
    if (this.useCartSelectionMap) {
      this.cartService.selectionMap.events[extraValue].tickets[value] = checkboxChange.checked ? ChildSelectionState.ALL : ChildSelectionState.NONE;
      this.cartService.updateSelectionMapStates(this.cartService.selectionMap);
      this.cartService.updateTotalScrapSum();
      this.renderUI();
    } else {
      if (checkboxChange.checked) {
        this.selected = [...this.selected, value];
      } else {
        this.selected = this.selected.filter(v => v !== value);
      }
    }
  }

  public formatText(format: string, item: any, useModel?: boolean): string {
    if (useModel) {
      return item[format] || '-';
    }
    return format || '-';
  }

  public formatNumber(format: string, item: any, useModel?: boolean): number {
    if (useModel) {
      return item[format] || 0;
    }
    return item || 0;
  }

  public evaluate(code: string, item: any): boolean {
    return !!eval(code); // ignore direct eval warning, since indirect usage won't work here, will make a QoL later to re-think solution to avoid this
  }

  public getRowClass(item: any): string {
    const rowClass = this.config.body.rowClass;
    if (typeof rowClass === 'string') {
      return rowClass;
    } else if (!rowClass) {
      return '';
    }
    const classes: string[] = [];
    rowClass.forEach(q => {
      const variable = q.func(item[q.prop]);
      const match = q.match;
      if ((variable && match) || (!variable && !match)) {
        classes.push(q.cssClass);
      }
    });
    return classes.join(' ');
  }

  public getIsHidden(model: DataTableColumn | DataTableRowItemButton, item: any): boolean {
    if (typeof model.isHidden === 'function') {
      return model.isHidden(item);
    }
    return model.isHidden;
  }

  public getHeaderText(key: string): string {
    const headerColumn: DataTableColumn = this.config.header.items.find(header => header.key === key);
    return headerColumn ? this.formatText(headerColumn.format, headerColumn, headerColumn.useModel) : '';
  }
  public getHeaderShowCurrency(key: string): boolean {
    const headerColumn: DataTableColumn = this.config.header.items.find(header => header.key === key);
    return headerColumn?.showCurrency;
  }

  public getHeaderCssClass(key: string): string {
    const headerColumn: DataTableColumn = this.config.header.items.find(header => header.key === key);
    return (headerColumn ? headerColumn.cssClass : '') as string;
  }

  public getHeaderChildText(key: string, childKey: string): string {
    const headerColumn: DataTableColumn = this.config.header.items.find(header => header.key === key);
    const childColumn: DataTableColumn = headerColumn.children.find(header => header.key === childKey);
    return childColumn ? this.formatText(childColumn.format, childColumn, childColumn.useModel) : '';
  }
  public getHeaderChildShowCurrency(key: string, childKey: string): boolean {
    const headerColumn: DataTableColumn = this.config.header.items.find(header => header.key === key);
    const childColumn: DataTableColumn = headerColumn.children.find(header => header.key === childKey);
    return childColumn?.showCurrency;
  }

  public getHeaderChildCssClass(key: string, childKey: string): string {
    const headerColumn: DataTableColumn = this.config.header.items.find(header => header.key === key);
    const childColumn: DataTableColumn = headerColumn.children.find(header => header.key === childKey);
    return (childColumn ? childColumn.cssClass : '') as string;
  }

  private renderUI(): void {
    this.changeRef.markForCheck();
  }
}
